// Role config — mirrors backend/workflow.py.
// The dictionary is keyed by role for lookup; ALL_ROLES + MOBILE_ROLES are
// arrays used by routing / role-picker UIs.

export const ROLES = {
  super_admin:             { label: "Super Admin",              color: "#0F172A", tier: "business" },
  sales:                   { label: "Sales Rep",                color: "#4F46E5", tier: "business" },
  sales_coordination:      { label: "Sales Coordination",       color: "#7C3AED", tier: "business" },
  accounts:                { label: "Accounts",                 color: "#475569", tier: "business" },
  design:                  { label: "2D/3D Design",             color: "#0284C7", tier: "shopfloor" },
  production_design:       { label: "Production Design",        color: "#0369A1", tier: "shopfloor" },
  production_coordination: { label: "Production Coordinator",   color: "#CA8A04", tier: "business" },
  admin:                   { label: "Admin (Procurement)",      color: "#DB2777", tier: "business" },
  production:              { label: "Production",               color: "#F97316", tier: "shopfloor", mobile: true },
  installation:            { label: "Installation",             color: "#059669", tier: "shopfloor", mobile: true },
  dealer:                  { label: "Dealer",                   color: "#6D28D9", tier: "business" },
  store:                   { label: "Store Manager",            color: "#B45309", tier: "business" },

  // Legacy aliases kept so old audit-log entries still render
  drawing_team:            { label: "2D/3D Design (legacy)",    color: "#0284C7", tier: "shopfloor", legacy: true },
  production_team:         { label: "Production (legacy)",      color: "#F97316", tier: "shopfloor", legacy: true },
  installation_team:       { label: "Installation (legacy)",    color: "#059669", tier: "shopfloor", legacy: true },
  admin_team:              { label: "Admin (legacy)",           color: "#DB2777", tier: "business",  legacy: true },
};

export const ALL_ROLES = [
  "super_admin", "sales", "sales_coordination", "accounts",
  "design", "production_design", "production_coordination", "admin",
  "production", "installation", "dealer", "store",
];

// MOBILE_ROLES was used to force production + installation onto the legacy
// /m mobile view. Per user request (2026-02) the desktop Dashboard was
// rebuilt for those roles (ProductionBoardDashboard), so we no longer auto-
// redirect. Kept as an empty list to avoid touching every import site.
export const MOBILE_ROLES = [];

export const BUSINESS_TIER_ROLES = new Set([
  "super_admin", "accounts", "sales", "sales_coordination", "production_coordination", "dealer",
]);
export const SHOPFLOOR_TIER_ROLES = new Set(["design", "production_design", "production", "installation"]);

export function isBusinessTier(role) {
  return BUSINESS_TIER_ROLES.has(role);
}

// ---- Enquiry stages (2026-02 pipeline restructure) ----
export const ENQUIRY_STAGES = [
  { key: "new",               label: "New",                          color: "#0F172A", bg: "#e2e8f0" },
  { key: "assigned",          label: "Assigned to Sales",             color: "#1d4ed8", bg: "#dbeafe" },
  { key: "attended",          label: "Attended — with Sales Coord",   color: "#c2410c", bg: "#ffedd5" },
  { key: "drawing",           label: "2D/3D Drawings",                color: "#0284C7", bg: "#e0f2fe" },
  { key: "drawings_review",   label: "2D/3D Sales Review",            color: "#EA580C", bg: "#ffedd5" },
  { key: "quote_prep",        label: "Quote Preparation",             color: "#7c3aed", bg: "#ede9fe" },
  { key: "sales_review",      label: "Sales Review",                  color: "#4F46E5", bg: "#e0e7ff" },
  { key: "customer_approval", label: "Awaiting Customer",             color: "#CA8A04", bg: "#fef3c7" },
  { key: "quoted",            label: "Quoted (legacy)",               color: "#7c3aed", bg: "#ede9fe" },
  { key: "converted",         label: "Converted to Project",          color: "#15803d", bg: "#dcfce7" },
  { key: "lost",              label: "Lost",                          color: "#b91c1c", bg: "#fee2e2" },
];
export const ENQUIRY_STAGES_BY_KEY = Object.fromEntries(ENQUIRY_STAGES.map((s) => [s.key, s]));

// ---- Sub-status (blocking) ----
export const SUB_STATUSES = [
  { key: "in_progress",   label: "In Progress",   color: "#059669", bg: "#d1fae5" },
  { key: "info_required", label: "Info Required", color: "#c2410c", bg: "#ffedd5" },
  { key: "on_hold",       label: "On Hold",       color: "#b91c1c", bg: "#fee2e2" },
  { key: "completed",     label: "Stage Complete",color: "#1d4ed8", bg: "#dbeafe" },
];
export const SUB_STATUSES_BY_KEY = Object.fromEntries(SUB_STATUSES.map((s) => [s.key, s]));
export const BLOCKING_SUB_STATUSES = new Set(["info_required", "on_hold"]);

// ---- Project stages (mirrors backend PROJECT_STAGES) ----
export const PROJECT_STAGES = [
  { key: "payment_verification",     label: "Payment Verification",     color: "#0F172A", bg: "#e2e8f0" },
  { key: "design_drawings",           label: "Production Design",        color: "#0284C7", bg: "#dbeafe" },
  { key: "customer_approval",         label: "Customer Approval (legacy)", color: "#c2410c", bg: "#ffedd5" },
  { key: "production_coordination",   label: "Production Coordination",  color: "#7c3aed", bg: "#ede9fe" },
  { key: "production",                label: "Production",               color: "#F97316", bg: "#ffedd5" },
  { key: "pre_installation",          label: "Pre-Installation",         color: "#DB2777", bg: "#fce7f3" },
  { key: "dispatch",                  label: "Packing & Dispatch",       color: "#CA8A04", bg: "#fef3c7" },
  { key: "installation",              label: "Installation",             color: "#059669", bg: "#d1fae5" },
  { key: "review_closeout",           label: "Review & Closeout",        color: "#4F46E5", bg: "#e0e7ff" },
  { key: "completed",                 label: "Completed",                color: "#15803d", bg: "#dcfce7" },
];
export const PROJECT_STAGES_BY_KEY = Object.fromEntries(PROJECT_STAGES.map((s) => [s.key, s]));
